<template>
  <el-card class="card linkFormSection">
    <div slot="header" class="linkFormSection__header dataRelationSection__header">
      <div class="dataRelationSection__headerMain">
        <strong class="linkFormSection__title">数据关联</strong>
        <span v-if="list.length" class="dataRelationSection__count">共 {{ list.length }} 项</span>
      </div>
      <p v-if="list.length && !disabled" class="dataRelationSection__tip">
        按住「拖动排序」列可调整顺序；字段名、页面选择与发布状态可正常阅读与点击。
      </p>
    </div>

    <div v-if="!list.length" class="dataRelationSection__empty">
      <i class="el-icon-connection dataRelationSection__emptyIcon" aria-hidden="true" />
      <p class="dataRelationSection__emptyTitle">暂无关联项</p>
      <p class="dataRelationSection__emptyDesc">
        请先选择关联应用，系统将按该 App 登记的数据集字段自动生成关联项。
      </p>
    </div>

    <div v-else class="dataRelationSection__table">
      <div
        class="dataRelationSection__head"
        :class="{ 'dataRelationSection__head--readonly': disabled }"
      >
        <span
          class="dataRelationSection__col dataRelationSection__col--index"
          :class="{ 'dataRelationSection__col--dragHead': !disabled }"
        >
          {{ disabled ? '#' : '拖动排序' }}
        </span>
        <span class="dataRelationSection__col dataRelationSection__col--name">字段名</span>
        <span class="dataRelationSection__col dataRelationSection__col--page">关联页面</span>
        <span class="dataRelationSection__col dataRelationSection__col--status">发布状态</span>
      </div>

      <div
        ref="relationList"
        class="dataRelationSection__body"
        :class="{ 'dataRelationSection__body--readonly': disabled }"
      >
        <div
          v-for="(item, index) in list"
          :key="item._rowUid || `${item.id}-${item.name}`"
          class="dataRelationSection__row"
          :class="{ 'dataRelationSection__row--missing': !item.pageId && !disabled }"
        >
          <div
            class="dataRelationSection__col dataRelationSection__col--index"
            :class="{ 'dataRelationSection__indexDragZone dataRelationSection__dragHandle': !disabled }"
            :aria-label="disabled ? undefined : '拖动排序'"
          >
            <i
              v-if="!disabled"
              class="el-icon-rank dataRelationSection__indexDragIcon"
              aria-hidden="true"
            />
            <span class="dataRelationSection__indexNum">{{ index + 1 }}</span>
          </div>

          <div class="dataRelationSection__col dataRelationSection__col--name">
            <span class="dataRelationSection__name" :title="item.name">{{ item.name }}</span>
          </div>

          <div class="dataRelationSection__col dataRelationSection__col--page dataRelationSection__interactive">
            <el-select
              v-model="item.pageId"
              class="dataRelationSection__pageSelect"
              placeholder="请选择页面"
              size="small"
              filterable
              clearable
              popper-class="linkListFilterDropdown"
              :disabled="disabled"
            >
              <el-option
                v-for="page in pageOptions"
                :key="page.id"
                :label="page.name"
                :value="page.id"
              />
            </el-select>
          </div>

          <div class="dataRelationSection__col dataRelationSection__col--status dataRelationSection__interactive">
            <data-live-status-choice
              :value="item.isOnLive ? '1' : '0'"
              :disabled="disabled"
              :aria-label="`${item.name} 发布状态`"
              @change="(value) => setLiveStatus(item, value)"
            />
          </div>
        </div>
      </div>
    </div>
  </el-card>
</template>

<script>
import Sortable from 'sortablejs'
import { getAllPage } from '../../../api/dataManage'
import DataLiveStatusChoice from './DataLiveStatusChoice.vue'

export default {
  name: 'DataRelationSection',
  components: {
    DataLiveStatusChoice,
  },
  props: {
    list: { type: Array, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      pageOptions: [],
      sortable: null,
    }
  },
  watch: {
    disabled(value) {
      if (value) {
        this.destroySortable()
      } else {
        this.$nextTick(() => {
          this.initSortable()
        })
      }
    },
    list: {
      immediate: true,
      handler(rows) {
        // 稳定行身份：只补 _rowUid，不把 index 编进 :key
        ;(rows || []).forEach((item) => {
          if (item && !item._rowUid) {
            this.$set(
              item,
              '_rowUid',
              `rel-${item.id != null ? item.id : 'x'}-${item.name || 'row'}-${Math.random()
                .toString(36)
                .slice(2, 8)}`
            )
          }
        })
        this.$nextTick(() => {
          if (!this.disabled) {
            this.initSortable()
          }
        })
      },
    },
  },
  mounted() {
    this.loadPageOptions()
    if (!this.disabled) {
      this.initSortable()
    }
  },
  beforeDestroy() {
    this.destroySortable()
  },
  methods: {
    setLiveStatus(item, value) {
      item.isOnLive = String(value) === '1'
    },
    loadPageOptions() {
      getAllPage().then((res) => {
        if (res.success) {
          this.pageOptions = res.data || []
        }
      })
    },
    initSortable() {
      const el = this.$refs.relationList
      if (!el || this.disabled) return
      this.destroySortable()
      this.sortable = Sortable.create(el, {
        handle: '.dataRelationSection__dragHandle',
        filter: '.dataRelationSection__interactive',
        preventOnFilter: true,
        ghostClass: 'dataRelationSection__row--ghost',
        chosenClass: 'dataRelationSection__row--chosen',
        dragClass: 'dataRelationSection__row--dragging',
        setData(dataTransfer) {
          dataTransfer.setData('Text', '')
        },
        onEnd: (evt) => {
          // 不直接改 prop：拷贝后 emit，由父级换新 list（单向数据流）
          const next = (this.list || []).slice()
          const targetRow = next.splice(evt.oldIndex, 1)[0]
          next.splice(evt.newIndex, 0, targetRow)
          this.$emit('update:list', next)
          this.$emit('change', next)
        },
      })
    },
    destroySortable() {
      if (this.sortable) {
        this.sortable.destroy()
        this.sortable = null
      }
    },
  },
}
</script>
